/**
 * Image URL Scanner Utility
 *
 * Scans HTML/CSS/JS content for image URLs and provides utilities
 * for URL replacement and file download.
 *
 * Used by ImageUrlScannerDialog to migrate external images to Segmently CDN.
 */

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Scanned image URL with context information
 */
export interface ScannedImageUrl {
  /** Original URL found in the HTML */
  url: string;
  /** Where the URL was found */
  context: 'img-src' | 'css-url' | 'js-string';
  /** Line number in the source (1-based) */
  lineNumber: number;
  /** Whether this URL is already on Segmently CDN */
  isSegmentlyCdn: boolean;
  /** Whether this URL is a local/relative path (not HTTP) */
  isLocalPath: boolean;
}

/**
 * URL mapping for migration tracking
 */
export interface UrlMapping {
  /** Original external URL or local path */
  originalUrl: string;
  /** New CDN URL after migration (empty if not yet uploaded) */
  newCdnUrl: string;
  /** Migration status */
  status: 'pending' | 'uploading' | 'success' | 'failed' | 'cors_blocked' | 'manual_upload' | 'local';
  /** Error message if failed or CORS blocked */
  error?: string;
  /** Whether this is a local/relative path */
  isLocalPath?: boolean;
  /** Multi-size asset URL (origin + small) — populated when using OnboardingMediaUploadService */
  assetUrl?: { origin: string; small: string };
  /** Original filename extracted from URL (for DataSource naming) */
  filename?: string;
}

/**
 * Mapping export format for JSON download
 */
export interface MappingExport {
  mappings: Array<{
    old: string;
    new: string;
    status: string;
    error?: string;
  }>;
  timestamp: string;
  successCount: number;
  failedCount: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Segmently CDN URL patterns
 * URLs matching these patterns are considered already migrated
 */
const SEGMENTLY_CDN_PATTERNS = [
  'https://api.segmently.ai/',
  'https://dev-api.segmently.ai/',
  'https://stage-api.segmently.ai/',
  'https://storage.googleapis.com/segmently-assets-',
];

/**
 * Image file extensions to detect
 */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'];

// ============================================================================
// REGEX PATTERNS
// ============================================================================

/**
 * Regex to find <img src="..."> tags
 * Captures the URL from src attribute
 */
const IMG_SRC_REGEX = /<img[^>]+src\s*=\s*["']([^"']+)["'][^>]*>/gi;

/**
 * Regex to find CSS url(...) patterns
 * Handles with/without quotes: url("..."), url('...'), url(...)
 */
const CSS_URL_REGEX = /url\(\s*["']?([^"')]+)["']?\s*\)/gi;

/**
 * Regex to find image URLs in JS strings
 * Matches strings ending with image extensions
 */
const JS_IMAGE_REGEX_PATTERN = `["']([^"'\\s]+\\.(${IMAGE_EXTENSIONS.join('|')}))["']`;
const JS_IMAGE_REGEX = new RegExp(JS_IMAGE_REGEX_PATTERN, 'gi');

// ============================================================================
// URL DETECTION FUNCTIONS
// ============================================================================

/**
 * Check if URL is on Segmently CDN
 *
 * @param url - URL to check
 * @returns true if URL is already on Segmently CDN
 */
export function isSegmentlyCdnUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  return SEGMENTLY_CDN_PATTERNS.some((pattern) => url.startsWith(pattern));
}

/**
 * Check if URL is a valid HTTP/HTTPS URL
 *
 * @param url - URL to validate
 * @returns true if URL is valid HTTP/HTTPS
 */
export function isValidHttpUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Check if URL points to an image file (by extension)
 *
 * @param url - URL to check
 * @returns true if URL has image extension
 */
export function hasImageExtension(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  const lowerUrl = url.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lowerUrl.includes(`.${ext}`));
}

/**
 * Check if URL is a local/relative path (not HTTP/HTTPS, not data:, not empty)
 *
 * Examples: "assets/hero.png", "./images/icon.svg", "img/bg.jpg"
 *
 * @param url - URL to check
 * @returns true if URL is a relative/local path with image extension
 */
export function isLocalImagePath(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  // Skip data URLs, HTTP URLs, protocol-relative URLs
  if (url.startsWith('data:') || url.startsWith('http:') || url.startsWith('https:') || url.startsWith('//')) {
    return false;
  }

  // Must have an image extension
  return hasImageExtension(url);
}

/**
 * Get line number for a string position
 *
 * @param content - Full content string
 * @param position - Character position (index)
 * @returns Line number (1-based)
 */
function getLineNumber(content: string, position: number): number {
  const beforePosition = content.substring(0, position);
  return (beforePosition.match(/\n/g) || []).length + 1;
}

// ============================================================================
// SCANNING FUNCTIONS
// ============================================================================

/**
 * Scan HTML content for image URLs
 *
 * Finds image URLs in:
 * - <img src="..."> tags
 * - CSS url(...) patterns
 * - JS string literals with image extensions
 *
 * @param html - HTML content to scan
 * @returns Array of scanned image URLs with metadata
 */
export function scanHtmlForImageUrls(html: string): ScannedImageUrl[] {
  if (!html || typeof html !== 'string') return [];

  const results: ScannedImageUrl[] = [];
  const seenUrls = new Set<string>();

  // Helper to add URL if not already seen
  const addUrl = (url: string, context: ScannedImageUrl['context'], position: number) => {
    // Skip if already seen (dedupe)
    if (seenUrls.has(url)) return;

    // Skip data URLs
    if (url.startsWith('data:')) return;

    const isLocal = isLocalImagePath(url);
    const isHttp = isValidHttpUrl(url);

    // Skip if neither a valid HTTP URL nor a local image path
    if (!isHttp && !isLocal) return;

    seenUrls.add(url);
    results.push({
      url,
      context,
      lineNumber: getLineNumber(html, position),
      isSegmentlyCdn: isHttp ? isSegmentlyCdnUrl(url) : false,
      isLocalPath: isLocal,
    });
  };

  // 1. Find <img src="..."> tags
  let match: RegExpExecArray | null;
  const imgRegex = new RegExp(IMG_SRC_REGEX.source, 'gi');
  while ((match = imgRegex.exec(html)) !== null) {
    const url = match[1];
    addUrl(url, 'img-src', match.index);
  }

  // 2. Find CSS url(...) patterns
  const cssRegex = new RegExp(CSS_URL_REGEX.source, 'gi');
  while ((match = cssRegex.exec(html)) !== null) {
    const url = match[1];
    // Only add if it looks like an image URL (has image extension or is HTTP)
    if (hasImageExtension(url) || isValidHttpUrl(url)) {
      addUrl(url, 'css-url', match.index);
    }
  }

  // 3. Find JS string literals with image extensions
  const jsRegex = new RegExp(JS_IMAGE_REGEX.source, 'gi');
  while ((match = jsRegex.exec(html)) !== null) {
    const url = match[1];
    addUrl(url, 'js-string', match.index);
  }

  // Sort by line number
  results.sort((a, b) => a.lineNumber - b.lineNumber);

  return results;
}

/**
 * Filter to only external URLs (not on Segmently CDN)
 *
 * @param urls - Array of scanned URLs
 * @returns Only URLs that need migration
 */
export function filterExternalUrls(urls: ScannedImageUrl[]): ScannedImageUrl[] {
  return urls.filter((item) => !item.isSegmentlyCdn);
}

// ============================================================================
// REPLACEMENT FUNCTIONS
// ============================================================================

/**
 * Replace URLs in HTML content using mappings
 *
 * Uses simple string replacement (not regex) for safety with special characters.
 * Only replaces URLs that have status 'success' or 'manual_upload' with a valid newCdnUrl.
 *
 * @param html - Original HTML content
 * @param mappings - URL mappings from migration
 * @returns HTML with replaced URLs
 */
export function replaceUrlsInHtml(html: string, mappings: UrlMapping[]): string {
  if (!html || !mappings || mappings.length === 0) return html;

  let result = html;

  // Only replace successful migrations (including manual uploads)
  const successfulMappings = mappings.filter(
    (m) => (m.status === 'success' || m.status === 'manual_upload') && m.newCdnUrl
  );

  // Sort by URL length (longest first) to avoid partial replacements
  const sortedMappings = [...successfulMappings].sort(
    (a, b) => b.originalUrl.length - a.originalUrl.length
  );

  for (const mapping of sortedMappings) {
    // Use global string replacement
    result = result.split(mapping.originalUrl).join(mapping.newCdnUrl);
  }

  return result;
}

/**
 * Count how many times each URL appears in HTML
 *
 * @param html - HTML content
 * @param urls - URLs to count
 * @returns Map of URL to occurrence count
 */
export function countUrlOccurrences(html: string, urls: string[]): Map<string, number> {
  const counts = new Map<string, number>();

  for (const url of urls) {
    const regex = new RegExp(escapeRegExp(url), 'g');
    const matches = html.match(regex);
    counts.set(url, matches ? matches.length : 0);
  }

  return counts;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ============================================================================
// LOCAL FILE MATCHING FUNCTIONS
// ============================================================================

/**
 * Match result for a local path matched to a user-selected file
 */
export interface LocalFileMatch {
  /** Original relative path from HTML */
  localPath: string;
  /** Matched File object from folder picker */
  file: File;
  /** How the match was made */
  matchType: 'exact' | 'filename';
}

/**
 * Match files from a folder selection to local paths found in HTML.
 *
 * Two-pass matching:
 * 1. Exact path match (e.g. "assets/hero.png" matches "MyFolder/assets/hero.png")
 * 2. Filename-only fallback (e.g. "assets/hero.png" matches "OtherFolder/hero.png")
 *
 * @param localPaths - Relative paths found in HTML (e.g. ["assets/hero.png", "assets/icon.svg"])
 * @param files - Files from folder picker (webkitdirectory) or drag & drop
 * @returns Array of matched files and array of unmatched paths
 */
export function matchLocalFilesToPaths(
  localPaths: string[],
  files: File[]
): { matched: LocalFileMatch[]; unmatched: string[] } {
  const matched: LocalFileMatch[] = [];
  const remainingPaths = new Set(localPaths);

  // Build lookup maps from files
  // webkitRelativePath: "RootFolder/assets/hero.png" → strip first segment → "assets/hero.png"
  const fileByRelativePath = new Map<string, File>();
  const filesByFilename = new Map<string, File[]>();

  for (const file of files) {
    const relativePath = file.webkitRelativePath || file.name;

    // Strip root folder from webkitRelativePath
    const segments = relativePath.split('/');
    const pathWithoutRoot = segments.length > 1 ? segments.slice(1).join('/') : segments[0];
    fileByRelativePath.set(pathWithoutRoot, file);

    // Also index by filename only
    const filename = segments[segments.length - 1];
    if (!filesByFilename.has(filename)) {
      filesByFilename.set(filename, []);
    }
    filesByFilename.get(filename)!.push(file);
  }

  // Pass 1: Exact path match
  for (const localPath of remainingPaths) {
    const normalizedPath = localPath.replace(/^\.\//, ''); // strip leading ./
    const file = fileByRelativePath.get(normalizedPath);
    if (file) {
      matched.push({ localPath, file, matchType: 'exact' });
      remainingPaths.delete(localPath);
    }
  }

  // Pass 2: Filename-only fallback
  for (const localPath of remainingPaths) {
    const filename = localPath.split('/').pop() || '';
    const candidates = filesByFilename.get(filename);
    if (candidates && candidates.length > 0) {
      // Use first candidate that hasn't been used
      const usedFiles = new Set(matched.map((m) => m.file));
      const available = candidates.find((f) => !usedFiles.has(f));
      if (available) {
        matched.push({ localPath, file: available, matchType: 'filename' });
        remainingPaths.delete(localPath);
      }
    }
  }

  return { matched, unmatched: Array.from(remainingPaths) };
}

// ============================================================================
// EXPORT/DOWNLOAD FUNCTIONS
// ============================================================================

/**
 * Generate mapping JSON for download
 *
 * @param mappings - URL mappings from migration
 * @returns JSON string with mapping data
 */
export function generateMappingJson(mappings: UrlMapping[]): string {
  const successCount = mappings.filter((m) => m.status === 'success').length;
  const failedCount = mappings.filter((m) => m.status === 'failed').length;

  const exportData: MappingExport = {
    mappings: mappings.map((m) => ({
      old: m.originalUrl,
      new: m.newCdnUrl || '',
      status: m.status,
      ...(m.error && { error: m.error }),
    })),
    timestamp: new Date().toISOString(),
    successCount,
    failedCount,
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * Download content as a text file
 *
 * Creates a Blob, generates a download link, and triggers download.
 *
 * @param content - File content
 * @param filename - Name for the downloaded file
 * @param mimeType - MIME type (default: text/plain)
 */
export function downloadAsFile(
  content: string,
  filename: string,
  mimeType: string = 'text/plain'
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // Append to body (required for Firefox)
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download mapping as JSON file
 *
 * @param mappings - URL mappings
 * @param filenamePrefix - Prefix for filename (default: 'url-mapping')
 */
export function downloadMappingJson(
  mappings: UrlMapping[],
  filenamePrefix: string = 'url-mapping'
): void {
  const json = generateMappingJson(mappings);
  const timestamp = new Date().toISOString().slice(0, 10);
  downloadAsFile(json, `${filenamePrefix}-${timestamp}.json`, 'application/json');
}

/**
 * Download updated HTML script
 *
 * @param html - Updated HTML content
 * @param filenamePrefix - Prefix for filename (default: 'custom-screen')
 */
export function downloadUpdatedScript(html: string, filenamePrefix: string = 'custom-screen'): void {
  const timestamp = new Date().toISOString().slice(0, 10);
  downloadAsFile(html, `${filenamePrefix}-${timestamp}.html`, 'text/html');
}
